import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router";
import {
  useJobTypes,
  useCreateJobType,
  useDeleteJobType,
  useHardDeleteJobType,
  useActivateJobType,
  useCloneJobType,
  type JobTypeSummary,
} from "../hooks/useJobTypes";
import { useJobTypeCategories } from "../categories/useJobTypeCategories";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useAuth } from "@/hooks/useAuth";
import type { Surface } from "@capture/shared";
import { CreateJobTypeDialog } from "../components/CreateJobTypeDialog";
import { CloneJobTypeDialog } from "../components/CloneJobTypeDialog";
import { AdminListHeader } from "../../../components/admin/AdminListHeader";
import { ConfirmModal } from "@/components/ConfirmModal";
import "../admin.css";

// --- Publish-state badge (C-28) ---

interface PublishBadgeProps {
  publishedVersionNumber: number | null;
  hasUnpublishedChanges: boolean;
}

function publishStateBadge(
  publishedVersionNumber: number | null,
  hasUnpublishedChanges: boolean,
): { label: string; variant: "neutral" | "success" | "warning" } {
  if (!publishedVersionNumber) {
    return { label: "Draft", variant: "neutral" };
  }
  if (!hasUnpublishedChanges) {
    return { label: `Published v${publishedVersionNumber}`, variant: "success" };
  }
  return {
    label: `Published v${publishedVersionNumber} · unpublished changes`,
    variant: "warning",
  };
}

function PublishStateBadge({ publishedVersionNumber, hasUnpublishedChanges }: PublishBadgeProps) {
  const { label, variant } = publishStateBadge(publishedVersionNumber, hasUnpublishedChanges);
  return (
    <span className={`jt-badge jt-badge--publish jt-badge--publish-${variant}`}>{label}</span>
  );
}

// --- Page ---

export function JobTypeListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [surfaceFilter, setSurfaceFilter] = useState<"all" | "standard" | "remittance">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<{ id: string; name: string } | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<JobTypeSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { items, isLoading, error, refresh } = useJobTypes(search || undefined);
  const { create, isSubmitting } = useCreateJobType();
  const { remove } = useDeleteJobType();
  const { remove: hardRemove } = useHardDeleteJobType();
  const { activate } = useActivateJobType();
  const { clone } = useCloneJobType();
  const { categories } = useJobTypeCategories();
  const { entitlements } = useEntitlements();
  const { user } = useAuth();
  // E-025 — admin-only hard delete, and only for a job type with zero
  // referencing batches (the API 409s otherwise; this just keeps the
  // affordance from being offered where it can't succeed).
  const isAdmin = user?.permissions?.includes("admin") ?? false;

  const handleCreate = async (data: {
    name: string;
    description?: string;
    documentCategory?: string;
    templateId?: string;
    categoryId?: string;
    surface?: Surface;
  }) => {
    const jt = await create(data);
    setShowCreate(false);
    navigate(`/admin/job-types/${jt.id}`);
  };

  // SP1 §6 — surface derivations. Grouping stays by categoryId; surface is a
  // filter axis, not a grouping axis. Filter BEFORE grouping so category
  // sections shrink (and empty out) naturally.
  const hasRemittance = items.some((jt) => jt.surface === "remittance");
  const remittanceLapsed = hasRemittance && entitlements !== null && !entitlements.remittance;
  const surfaceFiltered = surfaceFilter === "all"
    ? items
    : items.filter((jt) => (jt.surface ?? "standard") === surfaceFilter);

  // Build an ordered list of { category | null, items[] } groups.
  // Named categories are ordered by displayOrder then name; Ungrouped last.
  const groupedSections = useMemo(() => {
    const catMap = new Map(categories.map((c) => [c.id, c]));
    const grouped = new Map<string | null, typeof items>([[null, []]]);
    for (const cat of categories) grouped.set(cat.id, []);
    for (const jt of surfaceFiltered) {
      const key = jt.categoryId ?? null;
      const bucket = grouped.get(key) ?? grouped.get(null)!;
      bucket.push(jt);
    }
    const named = categories
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
      .filter((c) => (grouped.get(c.id)?.length ?? 0) > 0)
      .map((c) => ({ category: catMap.get(c.id) ?? c, items: grouped.get(c.id)! }));
    const ungrouped = grouped.get(null) ?? [];
    return ungrouped.length > 0
      ? [...named, { category: null, items: ungrouped }]
      : named;
  }, [categories, surfaceFiltered]);

  const handleDeactivate = async (id: string) => {
    await remove(id);
    refresh();
  };

  const handleActivate = async (id: string) => {
    await activate(id);
    refresh();
  };

  const handleClone = async (name: string) => {
    if (!cloneTarget) return;
    setIsCloning(true);
    try {
      const newJt = await clone(cloneTarget.id, name);
      setCloneTarget(null);
      navigate(`/admin/job-types/${newJt.id}`);
    } finally {
      setIsCloning(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await hardRemove(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="admin-page">
      <AdminListHeader
        title="Job Types"
        count={items.length}
        primaryAction={{ label: "+ New Job Type", onClick: () => setShowCreate(true) }}
      />
      <div className="jt-header-actions" style={{ padding: "0 16px 8px" }}>
        <input
          className="jt-search"
          type="text"
          placeholder="Search job types..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {hasRemittance && (
          <div className="jt-surface-filter" role="group" aria-label="Surface filter">
            {(["all", "standard", "remittance"] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`jt-surface-pill ${surfaceFilter === f ? "jt-surface-pill--active" : ""}`}
                onClick={() => setSurfaceFilter(f)}
              >
                {f === "all" ? "All" : f === "standard" ? "Standard" : "Remittance"}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="jt-list">
        {isLoading && (
          <div className="admin-loading"><div className="loading-spinner" /></div>
        )}

        {error && <div className="admin-error">Error: {error}</div>}

        {!isLoading && !error && items.length === 0 && (
          <div className="jt-empty">
            <p>No job types yet.</p>
            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              + New Job Type
            </button>
          </div>
        )}

        {groupedSections.map(({ category, items: sectionItems }) => (
          <div
            key={category?.id ?? "ungrouped"}
            className="jt-section"
          >
            <h3 className="jt-section-heading">
              {category?.name ?? "Ungrouped"}
            </h3>
            {sectionItems.map((jt) => (
              <div
                key={jt.id}
                data-jt-id={jt.id}
                className={`jt-card ${!jt.isActive ? "jt-card--inactive" : ""}`}
              >
                <div className="jt-card-header">
                  <span className="jt-card-name">{jt.name}</span>
                  {!jt.isActive && <span className="jt-badge jt-badge--inactive">Inactive</span>}
                  <PublishStateBadge
                    publishedVersionNumber={jt.publishedVersionNumber}
                    hasUnpublishedChanges={jt.hasUnpublishedChanges}
                  />
                  {jt.documentCategory && (
                    <span className="jt-badge">{jt.documentCategory}</span>
                  )}
                  {jt.surface === "remittance" && (
                    <span className="jt-badge jt-badge--remittance">Remittance</span>
                  )}
                </div>
                {jt.surface === "remittance" && remittanceLapsed && (
                  <div className="jt-dormant-notice" role="status">
                    Remittance subscription inactive — new batch intake is paused; in-flight
                    work can complete.
                  </div>
                )}
                <div className="jt-card-meta">
                  <span>{jt._count.pipelineSteps} steps</span>
                  <span>&middot;</span>
                  <span>{jt.fieldCount} fields</span>
                  <span>&middot;</span>
                  <span>{jt.rulesCount} rules</span>
                  {jt._count.batches > 0 && (
                    <>
                      <span>&middot;</span>
                      <span>{jt._count.batches} batches</span>
                    </>
                  )}
                </div>
                {jt.description && (
                  <div className="jt-card-desc">{jt.description}</div>
                )}
                {/* Followups #7 — explicit dual-action buttons replace the
                     row-click handler. Processing is the more common entry
                     (orange-filled) so it gets visual emphasis; Form is
                     secondary (transparent + border). */}
                <div className="jt-card-actions">
                  <Link
                    to={`/admin/job-types/${jt.id}`}
                    className="rb-btn rb-btn-save"
                  >
                    Processing →
                  </Link>
                  <Link
                    to={`/admin/forms/${jt.id}`}
                    className="jt-action-btn"
                  >
                    Form →
                  </Link>
                  <button
                    className="jt-action-btn"
                    onClick={() => setCloneTarget({ id: jt.id, name: jt.name })}
                  >
                    Clone
                  </button>
                  {jt.isActive ? (
                    <button
                      className="jt-action-btn jt-action-btn--danger"
                      onClick={() => handleDeactivate(jt.id)}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      className="jt-action-btn jt-action-btn--activate"
                      onClick={() => handleActivate(jt.id)}
                    >
                      Activate
                    </button>
                  )}
                  {/* E-025 — admin-only, and only offered when the job type is
                      genuinely unused (zero batches). A referenced job type
                      keeps deactivate-only — no Delete affordance shown. */}
                  {isAdmin && jt._count.batches === 0 && (
                    <button
                      className="jt-action-btn jt-action-btn--danger"
                      data-testid={`jt-delete-${jt.id}`}
                      onClick={() => setDeleteTarget(jt)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {showCreate && (
        <CreateJobTypeDialog
          onSubmit={handleCreate}
          onClose={() => setShowCreate(false)}
          isSubmitting={isSubmitting}
          categories={categories}
          remittanceEntitled={entitlements?.remittance ?? false}
        />
      )}

      {cloneTarget && (
        <CloneJobTypeDialog
          sourceName={cloneTarget.name}
          isSubmitting={isCloning}
          onSubmit={handleClone}
          onClose={() => setCloneTarget(null)}
        />
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete job type?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" has no batches and will be permanently removed. This cannot be undone.`
            : undefined
        }
        confirmLabel={isDeleting ? "Deleting…" : "Delete"}
        destructive
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
